enum ErrorCode {
  Success = 0, // Success
  ServerError = -999, // Internal server error
  ServerTimeout = -99, // Server timeout
}

export interface HttpResponse<T> {
  parsedBody: T;
  headers: Headers;
}

export interface APIResponse<T> {
  error?: number;
  error_msg?: string;
  data?: T;
}

function formatResponse<T>(response: any): APIResponse<T> {
  if (Number.isInteger(response.error)) {
    return response as APIResponse<T>;
  }
  return {
    error: ErrorCode.Success,
    data: response,
  };
}

async function http<T>(
  url: string,
  options: RequestInit = {}
): Promise<HttpResponse<T>> {
  const response: Response = await fetch(url, {
    ...options,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include',
  });

  // We can have headers here, just in case
  if (response.status >= 200 && response.status < 300) {
    const parsedBody = await response.json();

    return {
      parsedBody,
      headers: response.headers,
    };
  } else {
    throw new Error(response.statusText);
  }
}

export async function get<T>(
  url: string,
  options: RequestInit = {}
): Promise<APIResponse<T>> {
  try {
    const response = await http<T>(url, {
      method: 'GET',
      ...options,
    });

    return formatResponse(response.parsedBody);
  } catch (error) {
    return {
      error:
        error.name === 'timeout'
          ? ErrorCode.ServerTimeout
          : ErrorCode.ServerError,
      error_msg: error.message,
    };
  }
}

export async function post<T>(
  url: string,
  body: unknown,
  options: RequestInit = {}
): Promise<APIResponse<T>> {
  try {
    const response = await http<T>(url, {
      method: 'POST',
      body: JSON.stringify(body),
      ...options,
    });

    return formatResponse(response.parsedBody);
  } catch (error) {
    return {
      error:
        error.name === 'timeout'
          ? ErrorCode.ServerTimeout
          : ErrorCode.ServerError,
      error_msg: error.message,
    };
  }
}
